import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const [permRes, orgRes] = await Promise.all([
        api.get('/auth/permissions'),
        api.get('/organization')
      ]);
      setPermissions(permRes.data);
      setOrganization(orgRes.data);
    } catch (err) {
      console.error('Failed to fetch permissions/org:', err);
      // Set defaults for legacy users
      setPermissions({
        role: 'portfolio_director',
        permissions: { can_view_all_portfolios: true, can_view_all_projects: true, can_use_ai: true, can_export_data: true },
        enabled_modules: ['portfolio_management', 'project_overview', 'project_scheduling', 'resource_management', 'hse_management', 'quality_management', 'contracts_claims', 'procurement'],
        assigned_projects: []
      });
      setOrganization(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      await fetchPermissions();
    } catch (err) {
      // ─────────────────────────────────────────────────────────────
      // A 401 from /auth/me means the stored token is invalid on the
      // server (usually because JWT_SECRET rotated on a redeploy, or the
      // user was deleted). Silently falling back to the cached user
      // makes the UI look "logged in" while every subsequent API call
      // returns 401 → screens render blank and users see "no data".
      // Instead, drop the dead session so the UI shows the sign-in page.
      // Any non-401 error (network hiccup) still gets the cached fallback
      // so offline / flaky connections don't force a logout.
      // ─────────────────────────────────────────────────────────────
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('ciq_token');
        localStorage.removeItem('ciq_refresh_token');
        localStorage.removeItem('ciq_user');
        localStorage.removeItem('ciq_project_access');
        localStorage.removeItem('ciq_show_portfolio');
        localStorage.removeItem('ciq_default_project');
        setUser(null);
      } else {
        const stored = localStorage.getItem('ciq_user');
        if (stored) {
          try {
            setUser(JSON.parse(stored));
            await fetchPermissions();
          } catch { setUser(null); }
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('ciq_token', res.data.token || res.data.accessToken);
    localStorage.setItem('ciq_user', JSON.stringify(res.data.user));
    if (res.data.refreshToken) localStorage.setItem('ciq_refresh_token', res.data.refreshToken);
    if (res.data.projectAccess) localStorage.setItem('ciq_project_access', JSON.stringify(res.data.projectAccess));
    localStorage.setItem('ciq_show_portfolio', res.data.showPortfolio ? 'true' : 'false');
    localStorage.setItem('ciq_default_project', res.data.defaultProjectId || '');
    setUser(res.data.user);
    await fetchPermissions();
    return res.data;
  };

  // Helper to check if user should see project selector
  const shouldShowProjectSelector = () => {
    const showPortfolio = localStorage.getItem('ciq_show_portfolio');
    return showPortfolio === 'true';
  };

  const register = async (name, email, password) => {
    const res = await api.post('/auth/register', { name, email, password });
    localStorage.setItem('ciq_token', res.data.token);
    localStorage.setItem('ciq_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    await fetchPermissions();
    return res.data;
  };

  const setupOrganization = async (orgData) => {
    const res = await api.post('/organization/setup', orgData);
    localStorage.setItem('ciq_token', res.data.token);
    localStorage.setItem('ciq_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setOrganization(res.data.organization);
    await fetchPermissions();
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('ciq_token');
    localStorage.removeItem('ciq_user');
    setUser(null);
    setPermissions(null);
    setOrganization(null);
  };

  const setUserData = (userData) => {
    setUser(userData);
    localStorage.setItem('ciq_user', JSON.stringify(userData));
  };

  // Permission check helpers
  const hasPermission = (perm) => permissions?.permissions?.[perm] ?? false;
  const hasModule = (moduleId) => permissions?.enabled_modules?.includes(moduleId) ?? false;
  const isAdmin = () => permissions?.role === 'admin';
  const canAccessProject = (projectId) => {
    if (hasPermission('can_view_all_projects')) return true;
    return permissions?.assigned_projects?.includes(projectId) ?? false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      permissions,
      organization,
      loading, 
      login, 
      register, 
      setupOrganization,
      logout, 
      setUserData, 
      checkAuth,
      fetchPermissions,
      hasPermission,
      hasModule,
      isAdmin,
      canAccessProject,
      shouldShowProjectSelector
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
